<script setup lang="ts">
import { ref, computed } from 'vue';
import { IconCheckCircle } from '@/components/design/icons';
import type { ContractManagementContractInDb } from '@/api';
import ContractSelectTable from '@/components/contractManagement/ContractSelectTable.vue';
import { useGaiaToast } from '@/composables/useGaiaToast';

interface Props {
  show: boolean;
  matterUid: string;
}

interface Emits {
  (e: 'update:show', value: boolean): void;
  (e: 'assigned', contractUid: string): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();
const message = useGaiaToast();

const showModal = computed({
  get: () => props.show,
  set: (value: boolean) => {
    emit('update:show', value);
  },
});

const selectedContract = ref<ContractManagementContractInDb | null>(null);

function handleAssign(): void {
  if (!selectedContract.value) {
    message.warning('Please select a contract');
    return;
  }
  emit('assigned', selectedContract.value.uid);
  showModal.value = false;
}

function handleCancel(): void {
  showModal.value = false;
}
</script>

<template>
  <NModal
    v-model:show="showModal"
    preset="card"
    title="Assign Contract to Matter"
    style="width: 900px"
    :mask-closable="false"
  >
    <div class="modal-content">
      <ContractSelectTable
        v-model:selected="selectedContract"
        :active="showModal"
        :exclude-matter-uids="[matterUid]"
      />
    </div>

    <template #footer>
      <NSpace justify="end">
        <NButton @click="handleCancel">Cancel</NButton>
        <NButton type="primary" :disabled="!selectedContract" @click="handleAssign">
          <template #icon>
            <NIcon><IconCheckCircle /></NIcon>
          </template>
          Assign Contract
        </NButton>
      </NSpace>
    </template>
  </NModal>
</template>

<style scoped>
.modal-content {
  min-height: 400px;
}
</style>
